import React, { useCallback, useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'

import { useTranslation } from '../../../context/translationProvider'
import { AuthContext } from '../../../context/authProvider'
import { WithdrawBankAccountList } from '../../organisms/WithdrawBankAccountList'
import useNavbarHeight from '../../../hooks/useNavbarHeight'
import { PaymentTypes, BANK_ACCOUNT_TYPES } from '../../../utils/constants'

import {
  getLivenessStatus,
  requestBankAccountWithdraw,
} from '../../../adapters/payments'
import { toast } from '../../atoms'
import { usePreDepositModals } from '../../../hooks/usePreDepositModals'
import { If } from '../../atoms/if'
import {
  StyledWithdrawForm,
} from '../../organisms/WithdrawDrawer/style'
import { useEventTracking } from '../../../context/eventTrackingProvider'
import { WithdrawStepAmount } from '../WithdrawStepAmount'
import { WithdrawStepReview } from '../WithdrawStepReview'
import { WithdrawStepSalaryInfo } from '../WithdrawStepSalaryInfo'

const WITHDRAW_STEPS = {
  SALARY_INFO: 'salary-info',
  AMOUNT: 'amount',
  ACCOUNT: 'account',
  REVIEW: 'review',
}
const ERROR_CODES = {
  NOT_ENOUGH_BALANCE: 46,
  NOT_ALLOWED_FOR_WITHDRAW: 43,
}

export const BankAccountWithdrawForm = ({
  method,
  minimumAmount,
  maximumAmount,
  bankAccounts = [],
  startLoading,
  stopLoading,
  isMobile,
  isLoading,
  setGoBackHandler,
}) => {
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      withdrawAmount: "0",
    }
  })
  const { translate } = useTranslation()
  const withdrawAmount = watch('withdrawAmount')
  const [selectedBank, setSelectedBank] = useState()
  const { openLivenessModal } = usePreDepositModals()
  const { user } = useContext(AuthContext)
  const currencySymbol = user?.wallet?.currency?.symbol
  const navbarHeight = useNavbarHeight()
  const { trackEvent, EVENT_NAMES } = useEventTracking()
  const shouldSkipBankSelection = bankAccounts.length === 1 && withdrawAmount
  const hasNoWithdrawalAccount =
  bankAccounts.every(
    (account) => account.accountType === BANK_ACCOUNT_TYPES.SALARY_ACCOUNT
  )
  const [step, setStep] = useState(hasNoWithdrawalAccount ? WITHDRAW_STEPS.SALARY_INFO : WITHDRAW_STEPS.AMOUNT)

  let withdrawalStepsArray
  if (hasNoWithdrawalAccount) {
    withdrawalStepsArray = [WITHDRAW_STEPS.SALARY_INFO]
  } else if (bankAccounts.length === 1) {
    withdrawalStepsArray = [WITHDRAW_STEPS.AMOUNT, WITHDRAW_STEPS.REVIEW]
  } else {
    withdrawalStepsArray = [WITHDRAW_STEPS.AMOUNT, WITHDRAW_STEPS.ACCOUNT, WITHDRAW_STEPS.REVIEW]
  }
  const stepIndex = withdrawalStepsArray.indexOf(step)
  const previousStep =
    withdrawalStepsArray[stepIndex - 1] || withdrawalStepsArray[stepIndex]
  const nextStep =
    withdrawalStepsArray[stepIndex + 1] || withdrawalStepsArray[stepIndex]

  const goBack = useCallback(() => {
    setStep(previousStep)
  }, [previousStep])

  useEffect(() => {
    setGoBackHandler(stepIndex > 0 ? () => goBack : null)

    switch (step) {
      case WITHDRAW_STEPS.AMOUNT:
        trackEvent(EVENT_NAMES.WITHDRAWAL_AMOUNT_LOADED)
        break
      case WITHDRAW_STEPS.ACCOUNT:
        trackEvent(EVENT_NAMES.WITHDRAWAL_BANK_ACCOUNT_CHOICE_LOADED)
        break
      case WITHDRAW_STEPS.REVIEW:
        trackEvent(EVENT_NAMES.WITHDRAWAL_REVIEW_LOADED)
        break
      default:
        break
    }
  }, [step])

  const handleAmountContinue = () => {
    if (!withdrawAmount) return
    if (hasNoWithdrawalAccount) return

    if (shouldSkipBankSelection) {
      setSelectedBank(bankAccounts[0])
    }
    setStep(nextStep)
  }

  const handleSelectBank = (bank) => {
    setSelectedBank(bank)
    if (bank.accountType === BANK_ACCOUNT_TYPES.SALARY_ACCOUNT) return
    setStep(nextStep)
  }

  const handleChange = () => {
    setStep(WITHDRAW_STEPS.AMOUNT)
  }

  const onWithdraw = async () => {
    trackEvent(EVENT_NAMES.WITHDRAWAL_REQUESTED)
    startLoading()

    const response = await requestBankAccountWithdraw({
      bankAccountId: selectedBank.id,
      amount: Number(withdrawAmount),
      paymentMethodDetailId: method.paymentMethodDetailId,
    })

    if (!response.ok) {
      stopLoading()

      if (response?.error?.messageCode === ERROR_CODES.NOT_ENOUGH_BALANCE) {
        return toast.error(
          translate('payments.withdraw.error.notEnoughBalance')
        )
      }

      if (response?.error?.messageCode === ERROR_CODES.NOT_ALLOWED_FOR_WITHDRAW) {
        return toast.error(translate('payments.withdraw.error.notAllowed'))
      }

      return toast.error(response.error.message)
    }

    stopLoading()

    const {
      data: {
        id: transactionId,
        liveness: { url, onboardingId },
      },
    } = response

    trackEvent(
      EVENT_NAMES.LIVENESS_STARTED,
      { liveness_reason: 'Withdrawal' },
    )
    openLivenessModal({
      bankAccount: selectedBank,
      onCancelClose: () => {},
      onSuccessSave: () => pollLivenessStatus(onboardingId, transactionId),
      openOrigin: PaymentTypes.WITHDRAW,
      customURL: url,
      showSuccessToast: false,
    })
  }

  const pollLivenessStatus = async (onboardingId, transactionId) => {
    const response = await getLivenessStatus(onboardingId)

    if (!response.ok) {
      toast.error(response.error.message)
      return
    }

    const {
      data: { status },
    } = response

    if (status === 'APPROVED') {
      toast.success(translate('payments.withdraw.withdrawalSuccessful'))
      trackEvent(
        EVENT_NAMES.LIVENESS_COMPLETED,
        { liveness_reason: 'Withdrawal' },
      )
      trackEvent(EVENT_NAMES.WITHDRAWAL_REQUEST_SUCCESSFUL)
      return
    }

    if (status === 'REPROVED') {
      toast.error(translate('payments.receipt.error'))
      return
    }

    setTimeout(() => pollLivenessStatus(onboardingId, transactionId), 500)
  }

  const isValidAmount =
    withdrawAmount > 0 &&
    withdrawAmount >= minimumAmount &&
    withdrawAmount <= user?.wallet?.amount &&
    withdrawAmount <= maximumAmount

  const isAmountContinueDisabled =
    !isValidAmount || isLoading || hasNoWithdrawalAccount

  const isSubmitDisabled =
    !isValidAmount ||
    isLoading ||
    selectedBank?.accountType === BANK_ACCOUNT_TYPES.SALARY_ACCOUNT

  return (
    <>
      <StyledWithdrawForm
        onSubmit={handleSubmit(onWithdraw)}
        isMobile={isMobile}
        navbarHeight={navbarHeight}
        expand
      >
        <If
          condition={step === WITHDRAW_STEPS.SALARY_INFO}
          render={() => (
            <WithdrawStepSalaryInfo
            />
          )}
        />

        <If
          condition={step === WITHDRAW_STEPS.AMOUNT}
          render={() => (
            <WithdrawStepAmount
              isLoading={isLoading}
              isContinueDisabled={isAmountContinueDisabled}
              minimumAmount={minimumAmount}
              maximumAmount={maximumAmount}
              hasNoWithdrawalAccount={hasNoWithdrawalAccount}
              availableBalance={user?.wallet?.amount}
              currencySymbol={currencySymbol}
              onContinue={handleAmountContinue}
              errors={errors}
              control={control}
            />
          )}
        />

        <If
          condition={step === WITHDRAW_STEPS.ACCOUNT}
          render={() => (
            <WithdrawBankAccountList
              selectedBank={selectedBank}
              onSelectBank={handleSelectBank}
              bankAccounts={bankAccounts}
            />
          )}
        />

        <If
          condition={step === WITHDRAW_STEPS.REVIEW}
          render={() => (
            <WithdrawStepReview
              isLoading={isLoading}
              isMobile={isMobile}
              isSubmitDisabled={isSubmitDisabled}
              availableBalance={user?.wallet?.amount}
              currencySymbol={currencySymbol}
              selectedBank={selectedBank}
              withdrawAmount={withdrawAmount}
              onAmountChangeClick={handleChange}
            />
          )}
        />
      </StyledWithdrawForm>
    </>
  )
}

BankAccountWithdrawForm.propTypes = {
  method: PropTypes.object,
  minimumAmount: PropTypes.number,
  maximumAmount: PropTypes.number,
  bankAccounts: PropTypes.array,
  startLoading: PropTypes.func,
  stopLoading: PropTypes.func,
  isMobile: PropTypes.bool,
  isLoading: PropTypes.bool,
  setGoBackHandler: PropTypes.func,
}
